import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney, t } from '@aquabox/shared';
import type { MessageKey } from '@aquabox/shared';
import type { OrderSource } from '../../api/types';

type PanelSource = Extract<OrderSource, 'HAYAT_PANEL' | 'ABANT_PANEL'>;

const SOURCE_LABEL: Record<PanelSource, MessageKey> = {
  HAYAT_PANEL: 'panelRep.sourceHayat',
  ABANT_PANEL: 'panelRep.sourceAbant',
};

// Harf karesi yerine marka renginde nokta + ad (kanal pilleriyle aynı dil: "A"/"H" kaldırıldı).
const SOURCE_COLOR: Record<PanelSource, string> = {
  HAYAT_PANEL: 'var(--ch-hayat)',
  ABANT_PANEL: 'var(--ch-abant-ink)',
};

function SourceBadge({ source }: { source: PanelSource }): ReactElement {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span
        aria-hidden
        style={{ width: 8, height: 8, borderRadius: '50%', background: SOURCE_COLOR[source] }}
      />
      <span style={{ fontSize: 13, fontWeight: 600 }}>{t(SOURCE_LABEL[source])}</span>
    </span>
  );
}

/**
 * "Panelde teslim (bugün)" kartı (kullanıcı kararı 30.09.2026).
 *
 * Eskiden DEV karttı: kaynak başına KART (3 kolon), her birinde ad + tutar + adet +
 * ayrı toplam kutusu. Çok yer kaplıyordu ve boşken dev SERIF başlık taşıyordu. Yeni
 * tasarım: kaynak başına TEK SATIR (marka + teslim adedi + tutar + "işlenmeyen N → Stok"
 * bağlantısı), toplam alta küçük metin olarak iner. Boşsa kart YALNIZ ince metin.
 *
 * `panel.bySource` yalnız HANDLED_EXTERNALLY siparişlerini sayar (defter özeti) ve eski
 * davranışı korur: ham durumu okunamamış (null) kapalı siparişler delivered sayılır. Bu
 * kartın "Panelde teslim" başlığı buradan beslenir; yeni `today.bySource` daha sıkı
 * tanımıyla KPI kartlarına gider (kullanıcı kararı 30.09.2026).
 */
export function PanelTodayCard({
  panel,
}: {
  panel: {
    delivered: number;
    revenue: string;
    bySource: Array<{
      source: OrderSource;
      delivered: number;
      revenue: string;
      cancelled: number;
      pending: number;
    }>;
  };
}) {
  const visible = panel.bySource.filter(
    (s) => s.source === 'HAYAT_PANEL' || s.source === 'ABANT_PANEL',
  );
  if (panel.delivered === 0 && visible.every((s) => s.delivered === 0)) {
    return (
      <section className="card card-pad">
        <h2 className="section-title" style={{ marginBottom: 6 }}>
          {t('panelRep.todayTitle')}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          {t('panelRep.todayEmpty')}
        </p>
      </section>
    );
  }
  return (
    <section className="card card-pad">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <h2 className="section-title" style={{ margin: 0 }}>
          {t('panelRep.todayTitle')}
        </h2>
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('hayatRep.notPosted')}
        </span>
      </div>
      <ul style={{ listStyle: 'none', margin: 0 }}>
        {visible.map((s) => {
          const src = s.source as PanelSource;
          const unprocessed = s.delivered - 0; // panel kartında işlenmeyen tutulmuyor; "deftere alınmamış" kartın kendisi.
          return (
            <li
              key={src}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderTop: '1px solid var(--border)',
              }}
            >
              <SourceBadge source={src} />
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                }}
              >
                {t('panelUi.deliveredCount', { count: s.delivered })}
              </span>
              <span style={{ fontSize: 15, fontWeight: 700, minWidth: 90, textAlign: 'right' }}>
                {formatMoney(s.revenue)}
              </span>
              {unprocessed > 0 ? (
                <Link
                  to="/stok?tab=external"
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: 'var(--primary)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  İşlenmeyen {unprocessed} → Stok
                </Link>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          fontSize: 12.5,
          color: 'var(--text-secondary)',
        }}
      >
        <span>{t('rep.colTotal')}</span>
        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
          {formatMoney(panel.revenue)}
        </span>
      </div>
    </section>
  );
}
